import type { ButtonHTMLAttributes, ReactNode } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PageContent } from "../../components/Page";
import { CampingRoute } from "../../router/CampingRoute";
import { PICTURES } from "./pictures";

const StyledAspectRatio = ({
  ratio,
  children,
}: {
  ratio: number;
  children: ReactNode;
}) => (
  <div
    className="relative w-full rounded-[12px] overflow-hidden bg-black shadow-[0_0_12px_0_rgba(135,135,135,1)] group"
    style={{ paddingBottom: `${(1 / ratio) * 100}%` }}
  >
    {children}
  </div>
);

const SlideImage = ({ src, alt }: { src: string; alt: string }) => (
  <img
    src={src}
    alt={alt}
    className="absolute inset-0 h-full w-full object-contain transition duration-200 ease-in-out group-hover:scale-105"
  />
);

const OverlayControls = ({ children }: { children: ReactNode }) => (
  <div className="absolute left-1/2 bottom-4 -translate-x-1/2 inline-flex items-center justify-center gap-3 px-3 py-2 rounded-full bg-black/50 backdrop-blur-[8px] shadow-[0_6px_18px_rgba(0,0,0,0.35)]">
    {children}
  </div>
);

const Slideshow = ({
  children,
  onMouseEnter,
  onMouseLeave,
}: {
  children: ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => (
  <div
    className="flex flex-col gap-4 justify-center"
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </div>
);

const SlideFrame = ({ children }: { children: ReactNode }) => (
  <div className="w-full mx-auto md:max-w-[840px] lg:max-w-[980px]">
    {children}
  </div>
);

const Controls = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center justify-center gap-3">{children}</div>
);

const ControlButton = ({
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    {...props}
    className="rounded-full bg-[#1f1f1f] text-white p-2 text-base font-semibold transition duration-200 ease-in-out hover:bg-black hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#f3c76a] focus-visible:outline-offset-2"
  >
    {children}
  </button>
);

const Counter = ({ children }: { children: ReactNode }) => (
  <div className="text-sm text-white">{children}</div>
);

const Thumbnails = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
  }
>(({ children }, ref) => (
  <div
    ref={ref}
    className="flex gap-2.5 flex-nowrap overflow-x-auto overflow-y-hidden w-full max-w-[520px] md:max-w-[1210px] pb-2 snap-x snap-mandatory mx-auto"
  >
    {children}
  </div>
));

Thumbnails.displayName = "Thumbnails";

const ThumbnailButton = forwardRef<
  HTMLButtonElement,
  {
    active: boolean;
    children: ReactNode;
  } & ButtonHTMLAttributes<HTMLButtonElement>
>(({ active, children, ...props }, ref) => (
  <button
    {...props}
    ref={ref}
    className={`flex-shrink-0 border-2 rounded-[10px] p-0 bg-transparent cursor-pointer snap-start transition duration-200 ease-in-out hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#f3c76a] focus-visible:outline-offset-2 ${
      active ? "border-[#1f1f1f]" : "border-transparent"
    }`}
  >
    {children}
  </button>
));

ThumbnailButton.displayName = "ThumbnailButton";

const ThumbnailImage = ({ src, alt }: { src: string; alt?: string }) => (
  <img
    src={src}
    alt={alt ?? ""}
    className="block w-24 md:w-28 aspect-[4/3] object-contain bg-black rounded-[8px]"
  />
);

function Photos() {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const total = PICTURES.length;
  const thumbnailRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const thumbnailsContainerRef = useRef<HTMLDivElement | null>(null);

  const activePicture = useMemo(() => PICTURES[index], [index]);

  const goNext = useCallback(() => {
    setIndex((current) => (current + 1) % total);
  }, [total]);

  const goPrev = useCallback(() => {
    setIndex((current) => (current - 1 + total) % total);
  }, [total]);

  useEffect(() => {
    if (isPaused || total <= 1) {
      return;
    }

    const interval = window.setInterval(goNext, 6000);
    return () => window.clearInterval(interval);
  }, [goNext, isPaused, total]);

  useEffect(() => {
    const activeThumb = thumbnailRefs.current[index];
    const container = thumbnailsContainerRef.current;
    if (!activeThumb || !container) {
      return;
    }

    const targetLeft =
      activeThumb.offsetLeft -
      container.clientWidth / 2 +
      activeThumb.clientWidth / 2;
    container.scrollTo({
      behavior: "smooth",
      left: targetLeft,
    });
  }, [index]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrev();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev]);

  return (
    <PageContent>
      <Slideshow
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <SlideFrame>
          <StyledAspectRatio ratio={4 / 3}>
            <a href={activePicture.href} target="__blank">
              <SlideImage
                src={activePicture.href}
                alt="Camping des iles 2008"
              />
            </a>
            <OverlayControls>
              <Controls>
                <ControlButton
                  type="button"
                  onClick={goPrev}
                  aria-label="Photo précédente"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M15 5L8 12L15 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ControlButton>
                <Counter>
                  {index + 1} / {total}
                </Counter>
                <ControlButton
                  type="button"
                  onClick={goNext}
                  aria-label="Photo suivante"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M9 5L16 12L9 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </ControlButton>
              </Controls>
            </OverlayControls>
          </StyledAspectRatio>
        </SlideFrame>
        <Thumbnails ref={thumbnailsContainerRef}>
          {PICTURES.map((picture, pictureIndex) => (
            <ThumbnailButton
              key={picture.thumbnail}
              type="button"
              active={pictureIndex === index}
              onClick={() => setIndex(pictureIndex)}
              aria-label={`Voir la photo ${pictureIndex + 1}`}
              ref={(node) => {
                thumbnailRefs.current[pictureIndex] = node;
              }}
            >
              <ThumbnailImage src={picture.thumbnail} alt="" />
            </ThumbnailButton>
          ))}
        </Thumbnails>
      </Slideshow>
    </PageContent>
  );
}

export const PhotosRoute: CampingRoute = {
  title: "Photos",
  path: "/photos",
  element: <Photos />,
};
